import React from "react";
import { useInjection } from "@presentation/components/dependencyInjection";
import { SaveLicenseService } from "@application/useCases/license/SaveLicenseService";
import { TYPES } from "@utils/inversifyJS/types";
import type { SupportedSoftware } from "@domain/repositories/ISoftwareVersionsRepository";

/**
 * Outcome of resolving which search engines have a license in the cluster.
 *
 * `unknown` and `error` are deliberately distinct from `ready` with an empty list: the backend
 * caches a status reported asynchronously by the orchestrator, so "not answered yet" and "answered
 * with nothing licensed" must not be shown to the user the same way.
 */
export type LicensedSoftwareState = { status: "loading" } | { status: "unknown" } | { status: "error" } | { status: "ready"; licensed: SupportedSoftware[] };

const LOADING: LicensedSoftwareState = { status: "loading" };

const licensedSoftwareState: { cache: SupportedSoftware[] | null; inFlight: Promise<LicensedSoftwareState> | null } = {
  cache: null,
  inFlight: null,
};

/**
 * Shared across the job setup blocks so mounting several of them issues a single request.
 *
 * Only a determined status is cached; an unknown or failed lookup is retried by the next consumer.
 */
async function fetchLicensedSoftwareCached(service: SaveLicenseService): Promise<LicensedSoftwareState> {
  if (licensedSoftwareState.cache) {
    return { status: "ready", licensed: licensedSoftwareState.cache };
  }
  if (licensedSoftwareState.inFlight) {
    return licensedSoftwareState.inFlight;
  }

  licensedSoftwareState.inFlight = (async () => {
    try {
      const status = await service.getStatus();
      if (!status.known) {
        return { status: "unknown" };
      }

      const licensed: SupportedSoftware[] = [];
      if (status.spectronautLicensed) licensed.push("Spectronaut");
      if (status.spectroMineLicensed) licensed.push("SpectroMine");
      licensedSoftwareState.cache = licensed;
      return { status: "ready", licensed };
    } catch (error) {
      console.error("Could not load license status", error);
      return { status: "error" };
    } finally {
      licensedSoftwareState.inFlight = null;
    }
  })();

  return licensedSoftwareState.inFlight;
}

/**
Clears the cache so the next consumer re-reads the status (e.g. after adding a key).
*/
export function invalidateLicensedSoftwareCache(): void {
  licensedSoftwareState.cache = null;
}

/**
 * Whether a given engine is known to be licensed.
 *
 * Anything other than a determined `ready` status is treated as not licensed, so a job is never
 * submitted against an engine we could not confirm.
 */
export function isSoftwareLicensed(state: LicensedSoftwareState, software: string | undefined | null): boolean {
  return state.status === "ready" && !!software && state.licensed.includes(software as SupportedSoftware);
}

/**
The licensed engines, or `null` while the status is not determined yet.
*/
export function licensedSoftwareOrNull(state: LicensedSoftwareState): SupportedSoftware[] | null {
  return state.status === "ready" ? state.licensed : null;
}

/**
 * Search engine names disambiguate engine-scoped UI only when the license state
 * says the user can choose between zero/multiple engines, or while that state is
 * still resolving. A single resolved licensed engine has nothing to disambiguate.
 */
export function shouldQualifyWithEngineName(licensedSoftware: SupportedSoftware[] | null): boolean {
  return licensedSoftware === null || licensedSoftware.length !== 1;
}

export function useShouldShowEngineName(): boolean {
  return shouldQualifyWithEngineName(licensedSoftwareOrNull(useLicensedSoftware()));
}

/**
 * Returns the licensed search engines. While the orchestrator has not reported yet the status is
 * re-polled, because activation runs asynchronously in the cluster.
 */
export function useLicensedSoftware(pollIntervalMs = 5000): LicensedSoftwareState {
  const saveLicenseService = useInjection<SaveLicenseService>(TYPES.SaveLicenseService);
  const [state, setState] = React.useState<LicensedSoftwareState>(licensedSoftwareState.cache ? { status: "ready", licensed: licensedSoftwareState.cache } : LOADING);

  React.useEffect(() => {
    let isCancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const load = async () => {
      const next = await fetchLicensedSoftwareCached(saveLicenseService);
      if (isCancelled) return;
      setState(next);
      if (next.status !== "ready") {
        timer = setTimeout(load, pollIntervalMs);
      }
    };

    void load();

    return () => {
      isCancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [pollIntervalMs, saveLicenseService]);

  return state;
}
